import React, { useEffect, useState } from 'react';
import { RefreshCw, FileSignature, Clock, MailPlus, TrendingUp } from 'lucide-react';
import { AdminAPI } from '../../api';
import EmailDomainCard from './EmailDomainCard';
import { toast } from 'sonner';

const Card = ({ icon: Icon, label, value, hint, testId }) => (
  <div className="bg-white border border-[#E3E5F5] rounded-xl p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-[#6F76A7]">
      <Icon className="w-4 h-4" />
      <span className="text-[12px] font-semibold uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-[28px] font-bold text-[#0A1551] mt-2 leading-none">{value}</p>
    {hint && <p className="text-[12px] text-[#6F76A7] mt-1">{hint}</p>}
  </div>
);

const fmtWeek = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const StatsPanel = () => {
  const [data, setData] = useState(null);
  const load = () => AdminAPI.stats(8).then(setData).catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load stats'));
  useEffect(() => { load(); }, []);

  if (!data) return <p className="text-[13px] text-[#6F76A7]">Loading stats…</p>;
  const peak = Math.max(1, ...data.weekly.map((w) => w.count));

  return (
    <div data-testid="admin-stats">
      <div className="mb-4"><EmailDomainCard /></div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-[13px] text-[#6F76A7]">Signed agreements per week and how many people started but never finished.</p>
        <button onClick={load} data-testid="stats-refresh" className="inline-flex items-center gap-1.5 text-[13px] text-[#0A1551] hover:underline"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={FileSignature} label="Signed total" value={data.total_signed} hint={`${data.signed_last_7_days} in the last 7 days`} testId="stat-signed-total" />
        <Card icon={Clock} label="Unfinished" value={data.unfinished_total} hint={`${data.unfinished_last_7_days} started this week`} testId="stat-unfinished-total" />
        <Card icon={MailPlus} label="Reminded" value={data.unfinished_reminded} hint="Unfinished signers already chased" testId="stat-unfinished-reminded" />
        <Card icon={TrendingUp} label="Completion" value={`${data.completion_rate}%`} hint="Signed vs everyone who started" testId="stat-completion-rate" />
      </div>

      <div className="bg-white border border-[#E3E5F5] rounded-xl p-4 md:p-5 mt-4" data-testid="stats-weekly-chart">
        <h2 className="text-[15px] font-bold text-[#0A1551] mb-4">Weekly signups</h2>
        <div className="flex items-end gap-2 md:gap-4 h-[180px]">
          {data.weekly.map((w) => (
            <div key={w.week_start} className="flex-1 flex flex-col items-center justify-end gap-2" data-testid={`stats-week-${w.week_start}`}>
              <span className="text-[12px] font-semibold text-[#0A1551]">{w.count}</span>
              <div className="w-full rounded-t-md bg-[#0F766E] transition-all" style={{ height: `${Math.round((w.count / peak) * 130) + 4}px` }} />
              <span className="text-[11px] text-[#6F76A7] whitespace-nowrap">{fmtWeek(w.week_start)}</span>
            </div>
          ))}
        </div>
        <p className="text-[12px] text-[#6F76A7] mt-3">Weeks start on Monday (UTC).</p>
      </div>
    </div>
  );
};

export default StatsPanel;
